import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useToast } from "./toast";
import {
  getReportFilterEventsByYearService,
  getReportFilterYearsService,
} from "../service/reportFilter.service";

const useReportYearEventFilter = ({
  formRef,
  active,
  eventFieldName = "event",
  eventValueKey = "eventEditionCode",
  onYearChange,
} = {}) => {
  const { token } = useSelector((state) => state.auth);
  const intl = useIntl();
  const { addToast } = useToast();

  const [years, setYears] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loadingYears, setLoadingYears] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);

  const showApiError = useCallback(
    (err) => {
      addToast({
        type: "error",
        title: intl.formatMessage({ id: "error" }),
        description: err?.response?.data?.code
          ? intl.formatMessage({ id: "api.error." + err.response.data.code })
          : intl.formatMessage({ id: "error" }),
      });
    },
    [addToast, intl]
  );

  useEffect(() => {
    let mounted = true;

    setLoadingYears(true);
    getReportFilterYearsService({ token, active })
      .then((result) => {
        if (!mounted) {
          return;
        }
        setYears(
          (result?.data?.output ?? []).map((year) => ({
            label: String(year),
            value: year,
          }))
        );
      })
      .catch((err) => {
        if (!mounted) {
          return;
        }
        setYears([]);
        showApiError(err);
      })
      .finally(() => {
        if (mounted) {
          setLoadingYears(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [token, active, showApiError]);

  const loadEvents = useCallback(
    (year) => {
      if (year === null || year === undefined || year === "") {
        setEvents([]);
        return;
      }

      setLoadingEvents(true);
      getReportFilterEventsByYearService({ token, year, active })
        .then((result) => {
          setEvents(
            (result?.data?.output ?? []).map((event) => ({
              label: event.eventEditionName,
              value: event[eventValueKey],
              startDate: event.eventStartDate,
            }))
          );
        })
        .catch((err) => {
          setEvents([]);
          showApiError(err);
        })
        .finally(() => {
          setLoadingEvents(false);
        });
    },
    [token, active, eventValueKey, showApiError]
  );

  const onChangeYear = useCallback(
    (option) => {
      const year = option?.value ?? null;

      setSelectedYear(year);
      setEvents([]);
      formRef?.current?.setFieldValue(eventFieldName, "");
      loadEvents(year);

      if (onYearChange) {
        onYearChange(year);
      }
    },
    [formRef, eventFieldName, loadEvents, onYearChange]
  );

  const resetYearEventFilter = useCallback(() => {
    setSelectedYear(null);
    setEvents([]);
    formRef?.current?.setFieldValue("year", "");
    formRef?.current?.setFieldValue(eventFieldName, "");
  }, [formRef, eventFieldName]);

  const yearSelected = selectedYear !== null && selectedYear !== undefined;

  const noEventsForSelectedYear = useMemo(
    () => yearSelected && !loadingEvents && events.length === 0,
    [yearSelected, loadingEvents, events.length]
  );

  return {
    years,
    events,
    selectedYear,
    yearSelected,
    loadingYears,
    loadingEvents,
    noEventsForSelectedYear,
    eventFieldDisabled: !yearSelected || loadingEvents,
    onChangeYear,
    resetYearEventFilter,
  };
};

export default useReportYearEventFilter;
